import { type CSSProperties, useEffect, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router";
import { useDebouncedCallback } from "use-debounce";

import { TriggerButton, TriggerButtonGroup } from "widgets";
import { useHotKeys } from "widgets/hooks";

import style from "./styles/ItemPagination.module.scss";

import { Hotkeys } from "../../../settings/hotkeys.js";

interface IItemPaginationOwnProps {
  items: string[] | number[];
  selectedItem?: string | number;
  onPageChangeToRoute?: ({
    currentIndex,
    items,
  }: {
    currentIndex: number;
    items: IItemPaginationOwnProps["items"];
  }) => string;
  onPageChange?: ({ currentIndex, items }: { currentIndex: number; items: IItemPaginationOwnProps["items"] }) => void;
  hasKeyboard?: boolean;
  isUiSmall?: boolean;
  style?: CSSProperties;
}

function ItemPagination(props: IItemPaginationOwnProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // The current index of the array, so from 0 to length-1
  const [currentIndex, setCurrentIndex] = useState(-1);

  // Used only to keep the page input updated, simply currentIndex+1, but mandatory for rendering
  const [editValue, setEditValue] = useState(currentIndex + 1);
  const [isEditMode, setEditMode] = useState(false);

  // We debounce it to avoid sending too much requests.
  // The currently selected page number is updated independently of it (@see editValue)
  //const debounced = useDebouncedCallback(batchLog, 250, { 'maxWait': 1000 })
  const changeLocation = useDebouncedCallback(() => {
    if (typeof props.onPageChangeToRoute === "function") {
      const path = props.onPageChangeToRoute({
        currentIndex,
        items: props.items,
      });

      if (!matchPath({ path, end: false }, location.pathname)) {
        navigate(path);
      }
    } else if (typeof props.onPageChange === "function") {
      props.onPageChange({
        currentIndex,
        items: props.items,
      });
    }
  }, 300);

  // Disabling the back/forward buttons is controlled by
  // - our position within the current data chunk
  // - the position of the current data chunk within the full row set
  const isBackButtonDisabled = currentIndex <= 0;
  const isForwardButtonDisabled = currentIndex >= props.items.length - 1;

  useEffect(() => {
    setEditValue(currentIndex + 1);

    // We check again for the value, but it should never be wrong
    if (isNaN(currentIndex) || currentIndex < 0 || currentIndex > props.items.length - 1) {
      return;
    }

    // debounced
    changeLocation();
  }, [props.items, changeLocation, currentIndex]);

  useEffect(() => {
    setCurrentIndex(props.selectedItem ? props.items.findIndex((item) => item === props.selectedItem) : 0);
  }, [props.items, props.selectedItem]);

  // Global hotkeys as the navigation widget is in the Navigation Panel
  useHotKeys(Hotkeys.pagination.right, () => !isEditMode && props.hasKeyboard && onForward());
  useHotKeys(Hotkeys.pagination.rightWithOffset, () => !isEditMode && props.hasKeyboard && onForward(10));
  useHotKeys(Hotkeys.pagination.left, () => !isEditMode && props.hasKeyboard && onBack());
  useHotKeys(Hotkeys.pagination.leftWithOffset, () => !isEditMode && props.hasKeyboard && onBack(10));
  useHotKeys(Hotkeys.pagination.home, () => props.hasKeyboard && setCurrentIndex(0));
  useHotKeys(Hotkeys.pagination.end, () => props.hasKeyboard && setCurrentIndex(props.items.length - 1));

  function onBack(offset = 1) {
    const length = props.items.length;

    // onBack will loop indefinitely
    setCurrentIndex(Math.abs((currentIndex + length - offset) % length));
  }

  function onForward(offset = 1) {
    // onForward will loop indefinitely
    setCurrentIndex((currentIndex + offset) % props.items.length);
  }

  return (
    <div style={{ display: "flex", ...props.style }}>
      <TriggerButtonGroup>
        <TriggerButton
          onPress={() => onBack()}
          icon={"arrow_back"}
          label={"Previous"}
          isDisabled={isBackButtonDisabled}
        />
        {props.isUiSmall !== true && (
          <div style={{ whiteSpace: "nowrap", margin: "0 3px" }}>
            <input
              className={style.input}
              type={"text"}
              size={5}
              value={editValue}
              onChange={(e) => {
                setEditValue(Number(e.target.value));
              }}
              onFocus={() => setEditMode(true)}
              onBlur={() => setEditMode(false)}
              onKeyDown={(event) => {
                // The input is controllable with keyboard

                if (event.key === "Escape") {
                  // Escape: Back to current page
                  setEditValue(currentIndex + 1);
                  return event.currentTarget.blur();
                }

                if (event.key === "Enter") {
                  // Enter: valid the form
                  const val = Number.parseInt(Number(event.currentTarget.value).toFixed(0));

                  // If the value of the input is invalid, we just close it and don't redirect at all
                  // We implicitly consider that the user is not doing crazy things
                  // because there is no place and needs for warning messages
                  if (val.toString().trim() === "" || isNaN(val) || val <= 0 || val > props.items.length) {
                    setEditValue(currentIndex + 1);
                  } else if (currentIndex + 1 === val) {
                    setEditValue(currentIndex + 1);
                  } else {
                    // The input is valid, redirection is done in the hook
                    setCurrentIndex(val - 1);
                  }

                  return event.currentTarget.blur();
                }
              }}
            />
            / {props.items.length}
          </div>
        )}
        <TriggerButton
          onPress={() => onForward()}
          isDisabled={isForwardButtonDisabled}
          icon={"arrow_forward"}
          label={"Next"}
        />
      </TriggerButtonGroup>
    </div>
  );
}

export default ItemPagination;
